import ReactGA from 'react-ga4';
import { observer } from 'mobx-react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import React, { Suspense, useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from "@mui/material/styles";
import PrivateRoute from './components/ProtectedComponent';
import { AuthProvider, useAuth } from './context/AuthContext';
import useChatwoot from './hooks/useChatwoot';
import ChatwootButton from './components/ChatwootButton';
import PageLoading from './components/PageLoading';
import AdminPageLoading from './components/admin/AdminPageLoading';
import LanguageProvider from './context/LanguageProvider';
import OrientationGuard from './components/OrientationGuard';
import AppBannerStack from './components/AppBannerStack';
import ContentEditorShell from './components/content-editor/ContentEditorShell';
import LongActionProvider from './components/shared/long-action/LongActionProvider';
import NotFoundPage from './components/errors/NotFoundPage';
import './style/floatingActions.css';

// Lazy-loaded pages (code splitting)
const Login = React.lazy(() => import('./pages/login'));
const CoursePage = React.lazy(() => import('./pages/CoursePage'));
const CourseOnboarding = React.lazy(() => import('./pages/CourseOnboarding'));
const NewUserForm = React.lazy(() => import('./pages/NewUserForm'));
const InfoPage = React.lazy(() => import('./pages/InfoPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const HelpPage = React.lazy(() => import('./pages/HelpPage'));
const UserSettings = React.lazy(() => import('./pages/userSettings'));
const UnderMaintenance = React.lazy(() => import('./pages/underMaintenance'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const Courses = React.lazy(() => import('./pages/courses'));
const KPIs = React.lazy(() => import('./pages/KPIs'));
const StaffCourses = React.lazy(() => import('./pages/StaffCourses'));
const EditScenario = React.lazy(() => import('./pages/EditScenario'));
const EditScenarioState = React.lazy(() => import('./pages/EditScenarioState'));
const ScenarioStatesPage = React.lazy(() => import('./pages/ScenarioStatesPage'));
const ScenarioConditionsPage = React.lazy(() => import('./pages/ScenarioConditionsPage'));
const ConversationHistory = React.lazy(() => import('./pages/ConversationHistory'));
const CourseSkills = React.lazy(() => import('./pages/CourseSkills'));
const EditSkillGroup = React.lazy(() => import('./pages/EditSkillGroup'));
const EditInfoPage = React.lazy(() => import('./pages/EditInfoPage'));
const EditLesson = React.lazy(() => import('./pages/EditLesson'));
const EditLessonStep = React.lazy(() => import('./pages/EditLessonStep'));
const EditLessonAnnotations = React.lazy(() => import('./pages/EditLessonAnnotations'));
const EditLessonVersion = React.lazy(() => import('./pages/EditLessonVersion'));
const EditLessonAnnotationDetail = React.lazy(() => import('./pages/EditLessonAnnotationDetail'));
const EditSkill = React.lazy(() => import('./pages/EditSkill'));
const Lesson = React.lazy(() => import('./pages/Lesson'));
const InteractiveTools = React.lazy(() => import('./pages/InteractiveTools'));
const AdminUserManagement = React.lazy(() => import('./pages/AdminUserManagement'));
const AdminBugReports = React.lazy(() => import('./pages/AdminBugReports'));
const AdminCosts = React.lazy(() => import('./pages/AdminCosts'));
const AvatarsVoices = React.lazy(() => import('./components/admin/avatars-voices/avatarsVoices'));
const AvatarSetup = React.lazy(() => import('./pages/AvatarSetup'));
const ProgramDetails = React.lazy(() => import('./pages/ProgramDetails'));
const EditUnit = React.lazy(() => import('./pages/EditUnit'));
const ComposerLlmQuestions = React.lazy(() => import('./pages/composer/LlmQuestions'));
const ComposerLlmQuestionDetail = React.lazy(() => import('./pages/composer/LlmQuestionDetail'));
const ComposerUserQuestions = React.lazy(() => import('./pages/composer/UserQuestions'));
const ComposerUserQuestionDetail = React.lazy(() => import('./pages/composer/UserQuestionDetail'));

function LazyRoute({ children, fallback }) {
  return <Suspense fallback={fallback ?? <PageLoading />}>{children}</Suspense>;
}

/** Tooltips should not stay open when the pointer moves onto the tooltip surface (native MUI hover-follow). */
const muiTheme = createTheme({
  components: {
    MuiTooltip: {
      defaultProps: {
        disableInteractive: true,
      },
    },
  },
});

function AppContent() {
  const { user } = useAuth();

  ReactGA.initialize('G-7B1ETTNQMB');

  // Initialize Chatwoot chat widget with user information
  useChatwoot(user);

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: window.location.pathname });
  }, []);

  // Profile refreshes must preserve the router used by pending navigation callbacks.
  const [router] = useState(() => createBrowserRouter([
    {
      path: "/onboarding",
      element: <LazyRoute><PrivateRoute Component={Onboarding} props={{ onboarding: true }} /></LazyRoute>,
    },
    {
      path: "/",
      element: <LazyRoute><PrivateRoute Component={Courses} /></LazyRoute>,
    },
    {
      path: "/login",
      element: <LazyRoute><Login /></LazyRoute>,
    },
    {
      path: "/scenario/:id",
      element: <LazyRoute><PrivateRoute Component={Lesson} /></LazyRoute>,
    },
    {
      path: "/session/:id",
      element: <LazyRoute><PrivateRoute Component={Lesson} /></LazyRoute>,
    },
    {
      path: "/newUser",
      element: <LazyRoute><PrivateRoute Component={NewUserForm} props={{ newUserForm: true }} /></LazyRoute>,
    },
    {
      path: "/avatar-setup",
      element: <LazyRoute><PrivateRoute Component={AvatarSetup} /></LazyRoute>,
    },
    {
      path: "/course/:id",
      element: <LazyRoute><PrivateRoute Component={CoursePage} /></LazyRoute>,
    },
    {
      path: "/course/:id/info",
      element: <LazyRoute><PrivateRoute Component={CourseOnboarding} /></LazyRoute>,
    },
    {
      path: "/info-page/:id",
      element: <LazyRoute><PrivateRoute Component={InfoPage} /></LazyRoute>,
    },
    {
      path: "/settings",
      element: <LazyRoute><PrivateRoute Component={UserSettings} /></LazyRoute>,
    },
    {
      path: "/underMaintenance",
      element: <LazyRoute><UnderMaintenance /></LazyRoute>,
    },
    {
      element: <LongActionProvider><PrivateRoute props={{ admin: true, contentProvider: true }} Component={ContentEditorShell} /></LongActionProvider>,
      children: [
        {
          path: "/composer/courses/new",
          element: <ProgramDetails />,
          handle: { contentEditorTarget: { kind: "course" } },
        },
        {
          path: "/composer/courses/:id",
          element: <ProgramDetails />,
          handle: { contentEditorTarget: { kind: "course", param: "id" } },
        },
        {
          path: "/composer/courses/:courseId/skills",
          element: <CourseSkills />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/:courseId/skills/new",
          element: <EditSkill />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/:courseId/skills/:skillId/edit",
          element: <EditSkill />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/:courseId/skill-groups/new",
          element: <EditSkillGroup />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/:courseId/skill-groups/:groupId/edit",
          element: <EditSkillGroup />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/:courseId/llm-questions",
          element: <ComposerLlmQuestions />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/:courseId/llm-questions/:questionId",
          element: <ComposerLlmQuestionDetail />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/:courseId/user-questions",
          element: <ComposerUserQuestions />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/:courseId/user-questions/new",
          element: <ComposerUserQuestionDetail />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/:courseId/user-questions/:fieldId",
          element: <ComposerUserQuestionDetail />,
          handle: { contentEditorTarget: { kind: "course", param: "courseId" } },
        },
        {
          path: "/composer/courses/edit-unit/new",
          element: <EditUnit />,
          handle: {
            contentEditorTarget: { kind: "unit", draftParentQuery: "courseId" },
          },
        },
        {
          path: "/composer/courses/edit-unit/:id",
          element: <EditUnit />,
          handle: { contentEditorTarget: { kind: "unit", param: "id" } },
        },
        {
          path: "/composer/courses/edit-scenario/:id/states",
          element: <ScenarioStatesPage />,
          handle: { contentEditorTarget: { kind: "scenario", param: "id" } },
        },
        {
          path: "/composer/courses/edit-scenario/:id/states/:stateId",
          element: <EditScenarioState />,
          handle: { contentEditorTarget: { kind: "scenario", param: "id" } },
        },
        {
          path: "/composer/courses/edit-scenario/:id/conditions",
          element: <ScenarioConditionsPage />,
          handle: { contentEditorTarget: { kind: "scenario", param: "id" } },
        },
        {
          path: "/composer/courses/edit-scenario/:id",
          element: <EditScenario />,
          handle: { contentEditorTarget: { kind: "scenario", param: "id", draftParentQuery: "unitId" } },
        },
        {
          path: "/composer/courses/edit-info-page/:id",
          element: <EditInfoPage />,
          handle: { contentEditorTarget: { kind: "infoPage", param: "id", draftParentQuery: "unitId" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id/step/:stepId/annotations/:annotationId",
          element: <EditLessonAnnotationDetail />,
          handle: { contentEditorTarget: { kind: "lesson", param: "id" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id/step/:stepId/annotations",
          element: <EditLessonAnnotations />,
          handle: { contentEditorTarget: { kind: "lesson", param: "id" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id/version/:versionId/step/:stepId/annotations/:annotationId",
          element: <EditLessonAnnotationDetail />,
          handle: { contentEditorTarget: { kind: "lesson", param: "id" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id/version/:versionId/step/:stepId/annotations",
          element: <EditLessonAnnotations />,
          handle: { contentEditorTarget: { kind: "lesson", param: "id" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id/version/:versionId/step/:stepId",
          element: <EditLessonStep />,
          handle: { contentEditorTarget: { kind: "lesson", param: "id" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id/step/:stepId",
          element: <EditLessonStep />,
          handle: { contentEditorTarget: { kind: "lesson", param: "id" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id/annotations/:annotationId",
          element: <EditLessonAnnotationDetail />,
          handle: { contentEditorTarget: { kind: "lesson", param: "id" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id/annotations",
          element: <EditLessonAnnotations />,
          handle: { contentEditorTarget: { kind: "lesson", param: "id" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id/version/:versionId",
          element: <EditLessonVersion />,
          handle: { contentEditorTarget: { kind: "lesson", param: "id" } },
        },
        {
          path: "/composer/courses/edit-lesson/:id",
          element: <EditLesson />,
          handle: {
            contentEditorTarget: { kind: "lesson", param: "id", draftParentQuery: "unitId" },
          },
        },
      ],
    },
    {
      path: "/admin",
      children: [
        {
          index: true,
          element: <LongActionProvider><LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: true }} Component={StaffCourses} /></LazyRoute></LongActionProvider>,
        },
        {
          path: "/admin/KPIs",
          element: <LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: true }} Component={KPIs} /></LazyRoute>,
        },
        {
          path: "/admin/courses",
          element: <LongActionProvider><LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: true }} Component={StaffCourses} /></LazyRoute></LongActionProvider>,
        },
        {
          path: "/admin/conversation-history",
          element: <LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: true }} Component={ConversationHistory} /></LazyRoute>,
        },
        {
          path: "/admin/interactive-tools",
          element: <LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: false }} Component={InteractiveTools} /></LazyRoute>,
        },
        {
          path: "/admin/user-management",
          element: <LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: false }} Component={AdminUserManagement} /></LazyRoute>,
        },
        {
          path: "/admin/bugs",
          element: <LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: false }} Component={AdminBugReports} /></LazyRoute>,
        },
        {
          path: "/admin/costs",
          element: <LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: false }} Component={AdminCosts} /></LazyRoute>,
        },
        {
          path: "/admin/avatars-voices",
          element: <LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: false }} Component={AvatarsVoices} /></LazyRoute>,
        },
      ]
    },
    {
      path: "/composer",
      element: <LongActionProvider><LazyRoute fallback={<AdminPageLoading />}><PrivateRoute props={{ admin: true, contentProvider: true }} Component={StaffCourses} /></LazyRoute></LongActionProvider>,
    },
    {
      path: "/help",
      element: <LazyRoute><PrivateRoute Component={HelpPage} /></LazyRoute>,
    },
    {
      path: "/privacy-policy",
      element: <LazyRoute><PrivacyPolicyPage /></LazyRoute>,
    },
    {
      // Unknown URL: render the 404 in place rather than bouncing to a /404 route, so the address
      // bar keeps the link the user actually followed.
      path: "*",
      element: <NotFoundPage />,
    },
  ], {
    future: {
      v7_startTransition: true,
    },
  }));

  return (
    <>
      <RouterProvider router={router} />
      <ChatwootButton />
    </>
  );
}

function App() {
  return (
    <ThemeProvider theme={muiTheme}>
      <LanguageProvider>
        <GoogleOAuthProvider clientId="521614827163-m65g5uc23vuidtjcspjt8femc0kdsn17.apps.googleusercontent.com">
          <AuthProvider>
            <AppBannerStack />
            <OrientationGuard />
            <AppContent />
          </AuthProvider>
        </GoogleOAuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default observer(App);
